import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

const CompanySetupForm = () => {
  const [formData, setFormData] = useState({
    id: '',
    companyName: '',
    ceo: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
    telephone: '',
    email: '',
    accountName: '',
    bank: '',
    IBAN: '',
    BIC: '',
    taxNumber: '',
    registerNumber: '',
    lexwareBaseUrl: '',
    VATIdentificationNumber: '',
    currency: '',
    taxRate: '',
    smtpHost: '',
    smtpPort: '',
    encryption: '',
    smtpUser: '',
    smtpPass: '',
    lwLicensePartnerId: '',
    lwUsername: '',
    lwPassword: '',
    signatureImage: '',  // Base64-kodiertes Signaturbild
    headerImage: '',      // Base64-kodiertes Headerbild
    deliveryNoteTitle: '',
    deliveryNoteTitleCustomer: '',
    deliveryNoteTitleInternal: '',
    deliveryNoteNotice: '',
    deliveryNote2Title: '',
    deliveryNote2TitleCustomer: '',
    deliveryNote2TitleInternal: '',
    deliveryNote2Notice: '',
    documentTermsNotice: '',
    typePricingListNotice: '',
    customerTypePricingListNotice: '',
    deliveryNotePrintInternalCopy: true,
    deliveryNote2PrintInternalCopy: true,
    calendarAutoRefreshEnabled: true
  });
  const [activeTab, setActiveTab] = useState('general');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingImageReads, setPendingImageReads] = useState(0);

  // Laden der existierenden CompanySetup-Daten (Singleton-Annahme)
  useEffect(() => {
    api.get('/companysetups')
      .then(response => {
        if (response.data && response.data.length > 0) {
          const setup = response.data[0];
          setFormData(prev => ({
            ...prev,
            ...setup,
            calendarAutoRefreshEnabled: setup.calendarAutoRefreshEnabled !== false,
          }));
        }
      })
      .catch(error => {
        console.error('Fehler beim Laden der CompanySetup-Daten:', error);
        setLoadFailed(true);
        setMessage('Firmeneinstellungen konnten nicht geladen werden. Bitte Seite neu laden.');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Gemeinsame Funktion, um ausgewählte Bilder in Base64 umzuwandeln
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    setPendingImageReads(count => count + 1);
    reader.onload = () => {
      setFormData(prev => ({ ...prev, [fieldName]: reader.result }));
    };
    reader.onerror = () => {
      setMessage('Bild konnte nicht gelesen werden. Bitte erneut auswaehlen.');
    };
    reader.onloadend = () => {
      setPendingImageReads(count => Math.max(0, count - 1));
    };
    reader.readAsDataURL(file);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || loadFailed || isSaving || pendingImageReads > 0) return;

    setIsSaving(true);
    try {
      // Erstelle ein neues Objekt basierend auf formData und konvertiere numerische Felder
      const payload = {
        ...formData,
        taxRate: formData.taxRate ? parseFloat(formData.taxRate) : null,
        smtpPort: formData.smtpPort ? parseInt(formData.smtpPort, 10) : null,
      };
  
      // Wenn keine ID existiert, wird sie entfernt, damit ein neuer Datensatz erstellt wird
      if (!payload.id) {
        delete payload.id;
      }
  
      // Existiert bereits eine ID, wird per PUT aktualisiert (Bearbeiten)
      let response;
      if (formData.id) {
        response = await api.put(`/companysetups/${formData.id}`, payload);
      } else {
        response = await api.post('/companysetups', payload);
      }

      const savedSetup = response.data;
      setFormData(prev => ({
        ...prev,
        ...savedSetup,
        calendarAutoRefreshEnabled: savedSetup.calendarAutoRefreshEnabled !== false,
      }));
      setMessage('Einstellungen erfolgreich gespeichert!');
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      if (error.response?.status === 409) {
        setMessage('Die Firmeneinstellungen wurden zwischenzeitlich geaendert. Bitte Seite neu laden.');
      } else {
        setMessage('Fehler beim Speichern der Einstellungen');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  
  

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Helmet>
        <title>Firmeneinstellungen</title>
        <meta name="description" content="Company Setup Form" />
      </Helmet>
      <h2>Firmeneinstellungen</h2>
      {message && <p>{message}</p>}

      {/* Tab-Leiste, analog zur EmployeeForm */}
      <div className="tabs">
        {[
          { id: 'general', label: 'Allgemein' },
          { id: 'tax', label: 'Steuer' },
          { id: 'account', label: 'Bank' },
          { id: 'smtp', label: 'SMTP' },
          { id: 'lw', label: 'LW' },
          { id: 'documents', label: 'Dokumente' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalte */}
      {activeTab === 'general' && (
        <div className="form-row">
          <div className="col">
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Firmenname"
              required
            />
            <input
              type="text"
              name="ceo"
              value={formData.ceo}
              onChange={handleChange}
              placeholder="Geschäftsführer"
            />
            <input
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="Straße"
            />
          </div>
          <div className="col">
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              placeholder="PLZ"
            />
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Stadt"
            />
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Land"
            />
          </div>
          <div className="col">
            <input
              type="text"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              placeholder="Telefon"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
            />
            <label className="companysetup-checkbox">
              <input
                type="checkbox"
                name="calendarAutoRefreshEnabled"
                checked={formData.calendarAutoRefreshEnabled !== false}
                onChange={handleChange}
              />
              Kalender automatisch nachladen
            </label>
          </div>
        </div>
      )}

      {activeTab === 'tax' && (
        <div className="form-row">
          <div className="col">
            <input
              type="text"
              name="taxNumber"
              value={formData.taxNumber}
              onChange={handleChange}
              placeholder="Steuer-Nr."
            />
            <input
              type="text"
              name="registerNumber"
              value={formData.registerNumber}
              onChange={handleChange}
              placeholder="Handelsregisternummer"
            />
             {/* NEU: Lexware Base URL direkt unter Handelsregisternummer */}
            <input
              type="text"
              name="lexwareBaseUrl"
              value={formData.lexwareBaseUrl}
              onChange={handleChange}
              placeholder="Lexware Base URL (z.B. https://api.lexware.io)"
            />
          </div>
          <div className="col">
            <input
              type="text"
              name="VATIdentificationNumber"
              value={formData.VATIdentificationNumber}
              onChange={handleChange}
              placeholder="Umsatzsteuer-Identnr."
            />
          </div>
          <div className="col">
            <input
              type="text"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              placeholder="Währung"
            />
            <input
              type="number"
              name="taxRate"
              value={formData.taxRate}
              onChange={handleChange}
              placeholder="Steuersatz (%)"
              step="0.01"
            />
          </div>
        </div>
      )}


{activeTab === 'account' && (
        <div className="form-row">
          <div className="col">
          <input
              type="text"
              name="IBAN"
              value={formData.IBAN}
              onChange={handleChange}
              placeholder="IBAN"
      
            />

<input
              type="text"
              name="BIC"
              value={formData.BIC}
              onChange={handleChange}
              placeholder="BIC"
           
            />

<input
              type="text"
              name="bank"
              value={formData.bank}
              onChange={handleChange}
              placeholder="Bank"
            />
          </div>
        </div>
      )}

      {activeTab === 'smtp' && (
        <div className="form-row">
          <div className="col">
            <input
              type="text"
              name="smtpHost"
              value={formData.smtpHost}
              onChange={handleChange}
              placeholder="SMTP Host"
            />
          </div>
          <div className="col">
            <input
              type="number"
              name="smtpPort"
              value={formData.smtpPort}
              onChange={handleChange}
              placeholder="SMTP Port"
            />
            <input
              type="text"
              name="encryption"
              value={formData.encryption}
              onChange={handleChange}
              placeholder="Verschlüsselung"
            />
          </div>
          <div className="col">
            <input
              type="text"
              name="smtpUser"
              value={formData.smtpUser}
              onChange={handleChange}
              placeholder="SMTP User"
            />
            <input
              type="password"
              name="smtpPass"
              value={formData.smtpPass}
              onChange={handleChange}
              placeholder="SMTP Pass"
            />
          </div>
        </div>
      )}

      {activeTab === 'lw' && (
        <div className="form-row">
          <div className="col">
            <input
              type="text"
              name="lwLicensePartnerId"
              value={formData.lwLicensePartnerId || ''}
              readOnly
              title="Wird über LIFTWORKER_LICENSE_PARTNER_ID in der Backend-ENV verwaltet."
              placeholder="LW-ID-Lizenzpartner"
            />
            <small>Die LW-Lizenzpartnernummer wird vom Systemadministrator in der Backend-Konfiguration verwaltet.</small>

             {/* Upload für Headerbild */}
             <div className="upload-section">
              <label htmlFor="headerImage">Headerbild hochladen:</label>
              <input
                type="file"
                id="headerImage"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'headerImage')}
              />
              {/* Optional: Bildvorschau */}
              {formData.headerImage && (
                <img
                  src={formData.headerImage}
                  alt="Vorschau Headerbild"
                  style={{ maxWidth: '200px', marginTop: '10px' }}
                />
              )}
            </div>

          </div>
          <div className="col">
            <input
              type="text"
              name="lwUsername"
              value={formData.lwUsername}
              onChange={handleChange}
              placeholder="LW Benutzname"
            />
             {/* Upload für Signaturbild */}
             <div className="upload-section">
              <label htmlFor="signatureImage">Signatur ( 300x60 px )::</label>
              <input
                type="file"
                id="signatureImage"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'signatureImage')}
              />
              {/* Optional: Bildvorschau */}
              {formData.signatureImage && (
                <img
                  src={formData.signatureImage}
                  alt="Vorschau Signaturbild"
                  style={{ maxWidth: '200px', marginTop: '10px' }}
                />
              )}
            </div>
          </div>
          <div className="col">
            <input
              type="password"
              name="lwPassword"
              value={formData.lwPassword}
              onChange={handleChange}
              placeholder="LW Passwort"
            />

           



            

            







          </div>
        </div>
        
      )}

      {activeTab === 'documents' && (
        <div className="form-row">
          <div className="col">
            <label htmlFor="deliveryNoteTitle">Lieferschein Titel</label>
            <input
              type="text"
              id="deliveryNoteTitle"
              name="deliveryNoteTitle"
              value={formData.deliveryNoteTitle || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG"
            />
            <label htmlFor="deliveryNoteTitleCustomer">Lieferschein Titel Kunde</label>
            <input
              type="text"
              id="deliveryNoteTitleCustomer"
              name="deliveryNoteTitleCustomer"
              value={formData.deliveryNoteTitleCustomer || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG Kunde"
            />
            <label htmlFor="deliveryNoteTitleInternal">Lieferschein Titel Intern</label>
            <input
              type="text"
              id="deliveryNoteTitleInternal"
              name="deliveryNoteTitleInternal"
              value={formData.deliveryNoteTitleInternal || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG Intern"
            />
            <label htmlFor="deliveryNoteNotice">Lieferschein Note</label>
            <textarea
              id="deliveryNoteNotice"
              name="deliveryNoteNotice"
              value={formData.deliveryNoteNotice || ''}
              onChange={handleChange}
              placeholder="Hinweistext auf Lieferschein"
              rows="5"
            />
            <label className="companysetup-checkbox">
              <input
                type="checkbox"
                name="deliveryNotePrintInternalCopy"
                checked={formData.deliveryNotePrintInternalCopy !== false}
                onChange={handleChange}
              />
              LS1 interne Kopie drucken
            </label>
          </div>
          <div className="col">
            <label htmlFor="deliveryNote2Title">Lieferschein 2 Titel</label>
            <input
              type="text"
              id="deliveryNote2Title"
              name="deliveryNote2Title"
              value={formData.deliveryNote2Title || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG"
            />
            <label htmlFor="deliveryNote2TitleCustomer">Lieferschein 2 Titel Kunde</label>
            <input
              type="text"
              id="deliveryNote2TitleCustomer"
              name="deliveryNote2TitleCustomer"
              value={formData.deliveryNote2TitleCustomer || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG Kunde"
            />
            <label htmlFor="deliveryNote2TitleInternal">Lieferschein 2 Titel Intern</label>
            <input
              type="text"
              id="deliveryNote2TitleInternal"
              name="deliveryNote2TitleInternal"
              value={formData.deliveryNote2TitleInternal || ''}
              onChange={handleChange}
              placeholder="z.B. MIETVERTRAG Intern"
            />
            <label htmlFor="deliveryNote2Notice">Lieferschein Note2</label>
            <textarea
              id="deliveryNote2Notice"
              name="deliveryNote2Notice"
              value={formData.deliveryNote2Notice || ''}
              onChange={handleChange}
              placeholder="Hinweistext auf Lieferschein 2"
              rows="5"
            />
            <label className="companysetup-checkbox">
              <input
                type="checkbox"
                name="deliveryNote2PrintInternalCopy"
                checked={formData.deliveryNote2PrintInternalCopy !== false}
                onChange={handleChange}
              />
              LS2 interne Kopie drucken
            </label>
          </div>
          <div className="col">
            <label htmlFor="documentTermsNotice">Hinweistext Angebot/Auftrag/Rechnung</label>
            <textarea
              id="documentTermsNotice"
              name="documentTermsNotice"
              value={formData.documentTermsNotice || ''}
              onChange={handleChange}
              placeholder="Hinweistext direkt ueber dem Footer"
              rows="4"
            />
            <label htmlFor="typePricingListNotice">Hinweistext Typpreisliste</label>
            <textarea
              id="typePricingListNotice"
              name="typePricingListNotice"
              value={formData.typePricingListNotice || ''}
              onChange={handleChange}
              placeholder="Hinweistext direkt ueber dem Footer"
              rows="4"
            />
            <label htmlFor="customerTypePricingListNotice">Hinweistext Kundentyppreisliste</label>
            <textarea
              id="customerTypePricingListNotice"
              name="customerTypePricingListNotice"
              value={formData.customerTypePricingListNotice || ''}
              onChange={handleChange}
              placeholder="Hinweistext direkt ueber dem Footer"
              rows="4"
            />
          </div>
        </div>
      )}

<button
  type="submit"
  className="submit-button"
  disabled={isLoading || loadFailed || isSaving || pendingImageReads > 0}
>
  {isLoading
    ? 'Laden...'
    : pendingImageReads > 0
      ? 'Bild wird verarbeitet...'
      : isSaving
        ? 'Speichern...'
        : formData.id ? 'speichern' : 'Erstellen'}
</button>
    </form>
  );
};

export default CompanySetupForm;
